import React from "react";
import { AdsSlotProps } from "./Ads.Slot";
import { random } from "lodash";

const DEBUG_FLAG = "ojcdebugads";
const LOADED_SLOT_EVENT = "gaLoadSlot";
const CUSTOM_LOADED_EVENT = "gaCustomLoadSlot";
const DESKTOP_WIDTH_LIMIT = 980

const nthElementsCache = {}

type SlotProps = {
  id: string;
  className: string;
  slotId: string;
  isDebug: boolean;
  debugType: 'with_event' | 'without_event';
  isDebugNthSlot: boolean;
  type: string;
  componentReference: string;
  position: string;
  customHtml?: string;
  customEventName?: string;
  slotSizeMap: Object;
  targetName: string;
};
const Slot: React.FC<SlotProps> = ({
  id,
  className,
  slotId,
  isDebug,
  debugType = 'with_event',
  isDebugNthSlot = false,
  type,
  componentReference,
  position,
  customHtml,
  customEventName,
  slotSizeMap,
  targetName
}) => {
  const isMobile = className.indexOf("isMobile") >= 0
  // Alright slots
  const isHtmlAds = position === 'HTML'

  if (isHtmlAds) {
    const debugClassName = isDebug ? `alright-debug-slot ${isDebugNthSlot ? 'not-used ' : ''}${slotId}-${targetName}-${type}-${position}-${componentReference}` : undefined
    const debugText = isDebug ? `Alright AdSlot - #"${targetName}"-${slotId}-${type}-${position}-${componentReference} - notUsed: ${isDebugNthSlot}` : undefined

    const html = customHtml || ''
    const htmlAds =  isDebug ? `<p>${debugText}</p>` + html : html

    if (!!customEventName && !!window && debugType === 'with_event') {
      window.dispatchEvent(
        new CustomEvent(CUSTOM_LOADED_EVENT, {
          detail: {
            slotId,
            isMobile,
            id,
            position,
            customEventName
          },
        })
      );
    }

    return (
      <div id={'alright-adslot'} className={debugClassName} dangerouslySetInnerHTML={{ __html: htmlAds }}></div>
    )
  }

  if (window && debugType === 'with_event') {
    window.dispatchEvent(
      new CustomEvent(LOADED_SLOT_EVENT, {
        detail: {
          slotId,
          isMobile,
          id,
          position,
        },
      })
    );
  }

  let backgroundColor = "yellow";
  switch (type) {
    case "article":
      backgroundColor = "purple";
      break;
    case "block":
      backgroundColor = "blue";
      break;
    case "mega":
      backgroundColor = "green";
      break;
    case "side":
      backgroundColor = "red";
      break;
  }

  const minWidth = (!Array.isArray(slotSizeMap[slotId])) ? (slotSizeMap[slotId]?.[position]?.[0] ?? 300) : (slotSizeMap[slotId]?.[0] ?? 300);
  const minHeight = (!Array.isArray(slotSizeMap[slotId])) ? (slotSizeMap[slotId]?.[position]?.[1] ?? 250) : (slotSizeMap[slotId]?.[1] ?? 250);

  const style = {
    width: "100%",
    minWidth: isDebug ? `${minWidth || 300}px` : `${minWidth}px`,
    minHeight: isDebug ? `${minHeight || 250}px` : `${minHeight}px`,
    backgroundColor: isDebug ? backgroundColor : "transparent",
  };

  return (
    <div
      id={id}
      className={`${className}${isDebug ? ` debugAds ${slotId}-${targetName}-${type}-${position}-${componentReference}` : ""} ads-slot skeleton`}
      style={style}
    >
      {isDebug && (
        <p>{`"${targetName}" | ${type} | ${slotId} | ${position} | ${componentReference}`}</p>
      )}
    </div>
  );
};

interface AdsSlotEngineProps extends AdsSlotProps {
  settings: any;
  urlMapRegex: any;
  slotSizeMap: any;
}

const AdsSlotEngine: React.FC<AdsSlotEngineProps> = ({
  type,
  componentReference = "bottom",
  data,
  settings,
  urlMapRegex,
  slotSizeMap,
}) => {
  // URL params DEBUG_FLAG will enable the debug mode
  // if the query param is 2, the debug mode will show the slot without the event that triggers the slot load
  const isDebugMode = window?.location?.search?.indexOf(`${DEBUG_FLAG}`) >= 0;
  const debugType: SlotProps["debugType"] = window?.location?.search?.indexOf(`${DEBUG_FLAG}=2`) >= 0 ? 'without_event' : 'with_event';
  const isMobileView = window.innerWidth < DESKTOP_WIDTH_LIMIT
  let targetName = data?.name;
  let articleIndex = 0;

  // Type "mega", special case where the first element with rules will be used
  if (type === "mega") {
    const contentBlock = data?.contentBlock;
    const contentSide = data?.contentSide;

    if (contentBlock) {
      const blockItens = contentBlock.map((item) => item.name);
      for (let i = 0; i < blockItens.length; i++) {
        targetName = blockItens[i];
        break;
      }
    } else if (contentSide) {
      const sideItens = contentSide.map((item) => item.name);
      for (let i = 0; i < sideItens.length; i++) {
        targetName = sideItens[i];
        break;
      }
    }
  }
  else if (type === "article") {
    targetName = "Article Slot";

    articleIndex = data?.key ? (data.key < 3 ? data.key + 1 : 3) : 1;
  }
  else if (type === "search") {
    targetName = "Search Slot";
  }

  if (isDebugMode) {
    console.log(`[DEBUG] ads target slot: "${targetName}"`);
  }

  let showSlot = !!settings[targetName]
  let currentSlotSettings = showSlot ? settings[targetName] : settings.generic

  // Get current slot settings by url
  const pathname = window.location.pathname;
  const keys = Object.keys(urlMapRegex);
  let urlKey = "*";

  for (let i = 0; i < keys.length; i++) {
    const regex = new RegExp(keys[i]);

    if (regex.test(pathname)) {
      urlKey = urlMapRegex[keys[i]];
      break;
    }
  }
  
  currentSlotSettings = !!currentSlotSettings[urlKey] ? currentSlotSettings[urlKey] : !!currentSlotSettings["*"] ? currentSlotSettings['*'] : undefined
  if (!currentSlotSettings) {
    showSlot = false
    if (isDebugMode) {
      console.log(`[DEBUG] No slot settings found for url key: "${urlKey}"`);
    }
    currentSlotSettings = settings.generic['*']
  }

  currentSlotSettings = currentSlotSettings?.[type]?.[componentReference]
  if (!currentSlotSettings) {
    showSlot = false
    if (isDebugMode) {
      console.log(`[DEBUG] No slot settings found for type: "${type}" and component reference: "${componentReference}"`);
    }
  }

  if (isDebugMode && !showSlot) {
    currentSlotSettings = settings.generic['*']?.[type]?.[componentReference]
    if (currentSlotSettings) {
      console.log(`[DEBUG] Using generic settings for type: "${type}" and component reference: "${componentReference}"`);
      showSlot = true
    }
  }

  

  let idDesktop, classDesktop, slotNameDesktop, positionDesktop, nthPostionDesktop, customHtmlDesktop, customEventNameDesktop,  currentDesktopSlotEnabled;
  if (currentSlotSettings?.desktopSlot?.id) {
    const randomIdDsk = random(1000000, 9999999);
    const isArticle = type === "article";
    const position = isArticle ? currentSlotSettings.desktopSlot.position.replace('?', articleIndex) : currentSlotSettings.desktopSlot.position;
    const desktopSlotName =
      isArticle
        ? `${currentSlotSettings.desktopSlot.id} ${position}`
        : `${currentSlotSettings.desktopSlot.id}`;

    idDesktop = `${desktopSlotName}-${randomIdDsk}`;
    classDesktop = `isDesktop ${desktopSlotName.toLowerCase()}`;
    slotNameDesktop = currentSlotSettings.desktopSlot.id;
    positionDesktop = position;
    nthPostionDesktop = currentSlotSettings.desktopSlot?.nthPosition;
    customHtmlDesktop = currentSlotSettings.desktopSlot?.customHtml;
    customEventNameDesktop = currentSlotSettings.desktopSlot?.customEventName;
    currentDesktopSlotEnabled = true
  }

  let idMobile, classMobile, slotNameMobile, positionMobile, nthPositionMobile, customHtmlMobile, customEventNameMobile, currentMobileSlotEnabled;
  if (currentSlotSettings?.mobileSlot?.id) {
    const randomIdSmt = random(1000000, 9999999);
    const isArticle = type === "article";
    const position = isArticle ? currentSlotSettings.mobileSlot.position.replace('?', articleIndex) : currentSlotSettings.mobileSlot.position;
    const mobileSlotName =
      type === "article"
        ? `${currentSlotSettings.mobileSlot.id} ${position}`
        : `${currentSlotSettings.mobileSlot.id}`;

    idMobile = `${mobileSlotName}-${randomIdSmt}`;
    classMobile = `isMobile ${mobileSlotName.toLowerCase()}`;
    slotNameMobile = currentSlotSettings.mobileSlot.id;
    positionMobile = position;
    nthPositionMobile = currentSlotSettings.mobileSlot?.nthPosition;
    customHtmlMobile = currentSlotSettings.mobileSlot?.customHtml;
    customEventNameMobile = currentSlotSettings.mobileSlot?.customEventName;
    currentMobileSlotEnabled = true
  }

  let debugCurrentNthDesktopSlot = false
  let debugCurrentNthMobileSlot = false
  if (!!nthPostionDesktop || !!nthPositionMobile) {
    if (!nthElementsCache[targetName]) {
      nthElementsCache[targetName] = {}
    }

    if (!nthElementsCache[targetName][urlKey]) {
      nthElementsCache[targetName][urlKey] = {}
    }

    nthElementsCache[targetName][urlKey]["ocurrences"] =
      nthElementsCache[targetName][urlKey]["ocurrences"] ?
        nthElementsCache[targetName][urlKey]["ocurrences"] + 1 :
        1
    
    // TODO: Fix temporary because the screen is rendering twice
    if (nthElementsCache[targetName][urlKey]["ocurrences"] > 5) {
      nthElementsCache[targetName][urlKey]["ocurrences"] = 1
    }

    currentDesktopSlotEnabled = nthPostionDesktop.includes(nthElementsCache[targetName][urlKey]["ocurrences"])
    currentMobileSlotEnabled = nthPositionMobile.includes(nthElementsCache[targetName][urlKey]["ocurrences"])

    if (isDebugMode) {
      debugCurrentNthDesktopSlot = !currentDesktopSlotEnabled
      debugCurrentNthMobileSlot = !currentMobileSlotEnabled

      currentDesktopSlotEnabled = true
      currentMobileSlotEnabled = true
    }
  }

  if (isDebugMode) {
    const desktopSettings = {
      idDesktop,
      classDesktop,
      slotNameDesktop,
      positionDesktop,
      nthPostionDesktop,
      customHtmlDesktop,
      customEventNameDesktop
    };

    console.log("Current desktop settings:", desktopSettings);

    const mobileSettings = {
      idMobile,
      classMobile,
      slotNameMobile,
      positionMobile,
      nthPositionMobile,
      customHtmlMobile,
      customEventNameMobile
    };

    console.log("Current mobile settings:", mobileSettings);
    console.log('Is Mobile View? ', isMobileView);
  }

  return !showSlot ? null : (
    <>
      {!!currentSlotSettings?.desktopSlot?.id && currentDesktopSlotEnabled && !isMobileView && (
        <Slot
          id={idDesktop}
          className={classDesktop}
          slotId={slotNameDesktop}
          type={type}
          isDebug={isDebugMode}
          debugType={debugType}
          isDebugNthSlot={debugCurrentNthDesktopSlot}
          customHtml={customHtmlDesktop}
          customEventName={customEventNameDesktop}
          componentReference={componentReference}
          position={positionDesktop}
          slotSizeMap={slotSizeMap}
          targetName={targetName}
        />
      )}
      {!!currentSlotSettings?.mobileSlot?.id && currentMobileSlotEnabled && isMobileView && (
        <Slot
          id={idMobile}
          className={classMobile}
          slotId={slotNameMobile}
          type={type}
          isDebug={isDebugMode}
          debugType={debugType}
          isDebugNthSlot={debugCurrentNthMobileSlot}
          customHtml={customHtmlMobile}
          customEventName={customEventNameMobile}
          componentReference={componentReference}
          position={positionMobile}
          slotSizeMap={slotSizeMap}
          targetName={targetName}
        />
      )}
    </>
  );
};

export default AdsSlotEngine;
